/**
 * ImportarNominaHistorica.js
 *
 * Paso previo a la tabla de prenómina cuando se carga una nómina YA PAGADA en el
 * sistema anterior. Sube el Excel "Lista de Raya (forma tabular)" de CONTPAQi,
 * mapea sus columnas contra el catálogo de conceptos y crea la prenómina histórica.
 *
 * Todo el conocimiento del layout vive en parseoListaRaya.js (módulo puro): esta
 * pantalla solo orquesta y pinta. El backend recibe filas ya emparejadas y validadas,
 * igual que en ImportarEmpleados.
 */

import React, { Component } from 'react'
import { message, notification, Upload, Row, Col, Select, Spin, Alert, Tag, Tooltip, Modal, Table } from 'antd'
import { UploadOutlined, WarningOutlined, CheckCircleOutlined } from '@ant-design/icons'
import axios from 'axios'
import { connect } from 'react-redux'
import moment from 'moment'
import * as XLSX from 'xlsx'

import { ButtonItem, SelectItem, TableItem } from '../../../../Components/Items'
import { analizarHoja } from './parseoListaRaya'
import { codigosFaltantes } from './empleadosFaltantes'
import ModalEmpleadosFaltantes from './ModalEmpleadosFaltantes'

const { Option } = Select

const dinero = (n) =>
  (Number(n) || 0).toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })

class ImportarNominaHistorica extends Component {
  state = {
    loading: false,
    importando: false,

    // Catálogos
    periodos: [],
    conceptosNomina: [],
    empleados: [],
    prenominasPorPeriodo: [],
    mapeoGuardado: [],
    puedeGenerarPoliza: false,

    // Archivo
    archivos: [],
    libro: null,        // workbook de XLSX
    hojas: [],
    hojaSeleccionada: null,

    // Resultado del análisis
    analisis: null,     // { error, cabecera, mapeo, preview, conceptosIds, filas, resumen }
    mapeoManual: null,  // [{ indice, conceptoNominaId, ignorar }] cuando el usuario edita
    elecciones: {},     // { [filaExcel]: empleadoId } en renglones con varios candidatos

    periodoSeleccionado: null,

    // Ex-empleados que la lista de raya necesita y no existen en Biwo
    modalFaltantesVisible: false,
  }

  componentDidMount() {
    this.consultarCatalogos()
  }

  // `despues` corre ya con los catálogos nuevos en el estado: tras crear empleados
  // faltantes se usa para volver a emparejar la hoja sin perder periodo ni mapeo.
  consultarCatalogos(despues) {
    this.setState({ loading: true })
    axios
      .post(
        'prenomina/datosParaNominaHistorica',
        { empresaId: this.props.empresaId },
        { headers: this.props.headersToken }
      )
      .then((res) => {
        if (res.data.success === 2) {
          this.setState({
            periodos:             res.data.periodos || [],
            conceptosNomina:      res.data.conceptosNomina || [],
            empleados:            res.data.empleados || [],
            prenominasPorPeriodo: res.data.prenominasPorPeriodo || [],
            mapeoGuardado:        res.data.mapeoGuardado || [],
            puedeGenerarPoliza:   !!res.data.puedeGenerarPoliza,
          }, despues)
        } else {
          message.error(res.data.message || 'Error al cargar catálogos')
        }
        this.setState({ loading: false })
      })
      .catch(() => {
        message.error('Error al cargar catálogos')
        this.setState({ loading: false })
      })
  }

  // ── Archivo ────────────────────────────────────────────────────────────────

  onSubirArchivo({ file, onSuccess }) {
    const lector = new FileReader()
    lector.onload = (e) => {
      try {
        const libro = XLSX.read(e.target.result, { type: 'binary', cellDates: false })
        this.setState(
          {
            libro,
            hojas: libro.SheetNames,
            archivos: [{ uid: '1', name: file.name, status: 'done' }],
            analisis: null,
            mapeoManual: null,
            elecciones: {},
            hojaSeleccionada: null,
          },
          () => {
            // Si solo hay una hoja con pinta de lista de raya, seleccionarla sola.
            const candidatas = libro.SheetNames.filter((n) => this.hojaEsListaDeRaya(libro, n))
            if (candidatas.length === 1) this.onSeleccionarHoja(candidatas[0])
            else if (candidatas.length === 0) {
              message.warning('Ninguna hoja del archivo parece una lista de raya. Elige la hoja a mano.')
            }
          }
        )
        onSuccess('ok')
      } catch (err) {
        console.error('[nominaHistorica] error al leer el Excel:', err)
        message.error('No se pudo leer el archivo. Verifica que sea un .xlsx válido.')
      }
    }
    lector.readAsBinaryString(file)
  }

  // Una hoja sirve si tiene un renglón con "Código" y "Empleado". Se prueba con el
  // propio detector para no tener dos criterios distintos.
  hojaEsListaDeRaya(libro, nombreHoja) {
    try {
      const filas = XLSX.utils.sheet_to_json(libro.Sheets[nombreHoja], { header: 1, defval: '', raw: true })
      return !analizarHoja({
        filas,
        conceptosNomina: this.state.conceptosNomina,
        empleados: [],
        mapeoGuardado: [],
      }).error
    } catch {
      return false
    }
  }

  onSeleccionarHoja(nombreHoja) {
    this.setState({ hojaSeleccionada: nombreHoja, mapeoManual: null, elecciones: {} }, this.analizar)
  }

  analizar = () => {
    const { libro, hojaSeleccionada, conceptosNomina, empleados, mapeoGuardado, mapeoManual, elecciones } = this.state
    if (!libro || !hojaSeleccionada) return

    const filas = XLSX.utils.sheet_to_json(libro.Sheets[hojaSeleccionada], { header: 1, defval: '', raw: true })
    const analisis = analizarHoja({ filas, conceptosNomina, empleados, mapeoGuardado, mapeoManual, elecciones })

    if (analisis.error) message.error(analisis.error)
    this.setState({ analisis })
  }

  // Renglón con varios empleados posibles (mismo código o mismo nombre): el usuario
  // elige cuál. Por defecto parseoListaRaya propone el habilitado, si hay uno solo.
  elegirEmpleado(filaExcel, empleadoId) {
    this.setState(
      { elecciones: { ...this.state.elecciones, [filaExcel]: empleadoId } },
      this.analizar
    )
  }

  // ── Mapeo ──────────────────────────────────────────────────────────────────

  cambiarMapeo(indice, valor) {
    const { analisis } = this.state
    if (!analisis?.mapeo) return
    const nuevo = analisis.mapeo.map((c) =>
      c.indice === indice
        ? {
            ...c,
            conceptoNominaId: valor === '__ignorar__' ? null : valor,
            ignorar: valor === '__ignorar__',
          }
        : c
    )
    this.setState(
      { mapeoManual: nuevo.map((c) => ({ indice: c.indice, conceptoNominaId: c.conceptoNominaId, ignorar: c.ignorar })) },
      this.analizar
    )
  }

  // ── Importar ───────────────────────────────────────────────────────────────

  importar() {
    const { analisis, periodoSeleccionado } = this.state
    if (!analisis || analisis.error) return

    if (!periodoSeleccionado) {
      message.error('Elige el periodo al que corresponde este archivo')
      return
    }
    if (analisis.conceptosIds.length === 0) {
      message.error('No hay ninguna columna mapeada a un concepto de nómina')
      return
    }
    if (analisis.filas.length === 0) {
      message.error('Ningún renglón se pudo emparejar con un empleado')
      return
    }

    const { resumen } = analisis
    const avisos = []
    const sinEmpleado = resumen.conError - resumen.porElegir
    if (sinEmpleado) avisos.push(`${sinEmpleado} renglón(es) sin empleado — se van a omitir.`)
    if (resumen.porElegir) avisos.push(`${resumen.porElegir} renglón(es) con varios empleados posibles sin elegir — se van a omitir.`)
    if (resumen.columnasSinMapear) avisos.push(`${resumen.columnasSinMapear} columna(s) sin mapear — sus importes no se van a cargar.`)
    if (resumen.descuadrados) avisos.push(`${resumen.descuadrados} renglón(es) no cuadran contra los totales del archivo.`)

    const ejecutar = () => {
      this.setState({ importando: true })
      axios
        .post(
          'prenomina/importarNominaHistorica',
          {
            empresaId:    this.props.empresaId,
            periodoId:    periodoSeleccionado._id,
            conceptosIds: analisis.conceptosIds,
            mapeo: analisis.mapeo.map((c) => ({
              columna:          c.encabezado,
              tipo:             c.tipo,
              conceptoNominaId: c.conceptoNominaId,
              ignorar:          c.ignorar,
            })),
            filas: analisis.filas,
          },
          { headers: this.props.headersToken }
        )
        .then((res) => {
          this.setState({ importando: false })
          if (res.data.success === 2) {
            message.success(`Nómina histórica creada con ${res.data.lineasCreadas} empleados`)
            if (res.data.advertencias?.length) {
              notification.warning({
                message: 'Revisa el orden de carga',
                description: res.data.advertencias.join(' '),
                duration: null,
              })
            }
            this.props.onCreada(res.data.id)
          } else {
            message.error(res.data.message || 'Error al importar')
          }
        })
        .catch(() => {
          this.setState({ importando: false })
          message.error('Error al importar la nómina histórica')
        })
    }

    if (avisos.length) {
      Modal.confirm({
        title: '¿Importar de todos modos?',
        width: 560,
        content: (
          <div>
            {avisos.map((a, i) => <p key={i} style={{ marginBottom: 6 }}>{a}</p>)}
            <p style={{ marginTop: 12, marginBottom: 0 }}>
              Se van a cargar <b>{analisis.filas.length}</b> empleados con{' '}
              <b>{analisis.conceptosIds.length}</b> conceptos.
            </p>
          </div>
        ),
        okText: 'Importar',
        cancelText: 'Revisar',
        onOk: ejecutar,
      })
    } else {
      ejecutar()
    }
  }

  // ── Render ─────────────────────────────────────────────────────────────────

  columnasMapeo() {
    const { conceptosNomina } = this.state
    const percepciones = conceptosNomina.filter((c) => c.tipo === 1)
    const deducciones  = conceptosNomina.filter((c) => c.tipo === 2)
    const obligaciones = conceptosNomina.filter((c) => c.tipo === 3)

    const opciones = (tipo) => {
      // Se ofrecen primero los del mismo tipo que la columna, pero no se ocultan
      // los demás: hay catálogos donde una percepción del sistema anterior quedó
      // dada de alta como obligación.
      const orden = tipo === 1
        ? [['Percepciones', percepciones], ['Deducciones', deducciones], ['Obligaciones', obligaciones]]
        : [['Deducciones', deducciones], ['Percepciones', percepciones], ['Obligaciones', obligaciones]]
      // Children de una sola cadena: el filterOption compara sobre option.children,
      // y con children en array la búsqueda del select se rompe.
      return orden
        .filter(([, lista]) => lista.length)
        .map(([etiqueta, lista]) => (
          <Select.OptGroup key={etiqueta} label={etiqueta}>
            {lista.map((c) => (
              <Option value={String(c._id)} key={String(c._id)}>
                {`${c.serieNumero} · ${c.descripcion}`}
              </Option>
            ))}
          </Select.OptGroup>
        ))
    }

    return [
      {
        title: 'Columna del archivo', dataIndex: 'encabezado', key: 'encabezado', width: 230,
        render: (v, r) => (
          <span>
            {v}{' '}
            {r.control && <Tag color="default" style={{ fontSize: 10 }}>total de control</Tag>}
          </span>
        ),
      },
      {
        title: 'Tipo', dataIndex: 'tipo', key: 'tipo', width: 110,
        render: (v) => v === 1
          ? <Tag color="green">Percepción</Tag>
          : v === 2 ? <Tag color="volcano">Deducción</Tag> : <Tag>—</Tag>,
      },
      {
        title: 'Concepto de nómina', key: 'concepto', width: 340,
        render: (_, r) => r.control ? (
          // Las columnas *TOTAL* / *NETO* no se pueden mapear: son la suma de las
          // demás y cargarlas duplicaría todos los importes de la nómina.
          <span style={{ color: '#aaa', fontStyle: 'italic' }}>
            Se usa para validar, no se carga
          </span>
        ) : (
          <SelectItem
            style={{ width: '100%' }}
            value={r.ignorar ? '__ignorar__' : (r.conceptoNominaId || undefined)}
            placeholder="Sin mapear — no se va a cargar"
            showSearch
            allowClear={false}
            noFocusSiguiente
            filterOption={(input, opt) =>
              String(opt?.children || '').toLowerCase().includes(input.toLowerCase())
            }
            onChange={(v) => this.cambiarMapeo(r.indice, v)}
          >
            <Option value="__ignorar__" key="__ignorar__">— Ignorar esta columna —</Option>
            {opciones(r.tipo)}
          </SelectItem>
        ),
      },
      {
        title: 'Origen', dataIndex: 'origen', key: 'origen', width: 110,
        render: (v, r) => {
          if (r.ignorar) return <Tag>Ignorada</Tag>
          if (!r.conceptoNominaId) return <Tag color="red">Sin mapear</Tag>
          if (v === 'guardado') return <Tooltip title="Se recordó del último archivo importado"><Tag color="blue">Recordado</Tag></Tooltip>
          if (v === 'manual') return <Tag color="purple">Manual</Tag>
          return <Tooltip title="Empató por descripción con el catálogo"><Tag color="green">Automático</Tag></Tooltip>
        },
      },
      {
        title: 'Suma en el archivo', key: 'suma', width: 140, align: 'right',
        render: (_, r) => {
          const suma = (this.state.analisis?.preview || []).reduce(
            (s, p) => s + (p.importes[r.indice] || 0), 0
          )
          return <span style={{ fontFamily: 'monospace' }}>{dinero(suma)}</span>
        },
      },
    ]
  }

  // Una sola cadena: el filterOption de SelectItem compara contra option.children.
  etiquetaCandidato(c) {
    const estado = c.estado === 1 ? 'habilitado' : c.estado === 3 ? 'ficha incompleta' : 'deshabilitado'
    return [c.serieNumero, c.codigo != null ? `cód. ${c.codigo}` : null, c.nombre, c.rfc, estado]
      .filter(Boolean)
      .join(' · ')
  }

  selectorCandidatos(r) {
    const criterio = r.criterio === 'Código' ? 'código' : 'nombre'
    return (
      <div>
        <SelectItem
          style={{ width: '100%' }}
          size="small"
          // Si la elección manual chocó con otro renglón, se sigue mostrando lo elegido.
          value={r.empleadoId || this.state.elecciones[r.filaExcel] || undefined}
          placeholder={`${r.candidatos.length} empleados posibles — elige uno`}
          noFocusSiguiente
          onChange={(v) => this.elegirEmpleado(r.filaExcel, v)}
        >
          {r.candidatos.map((c) => (
            <Option value={c._id} key={c._id}>{this.etiquetaCandidato(c)}</Option>
          ))}
        </SelectItem>
        <div style={{ fontSize: 11, marginTop: 2, whiteSpace: 'normal', color: r.error ? '#cf1322' : '#888' }}>
          {r.error
            ? <><WarningOutlined /> {r.error}</>
            : r.eleccion === 'habilitado'
              ? `${r.candidatos.length} coincidencias por ${criterio}: se propuso el habilitado`
              : `${r.candidatos.length} coincidencias por ${criterio}: elegido a mano`}
        </div>
      </div>
    )
  }

  columnasPreview() {
    return [
      { title: 'Fila', dataIndex: 'filaExcel', key: 'filaExcel', width: 60 },
      { title: 'Código', dataIndex: 'codigo', key: 'codigo', width: 80 },
      { title: 'Empleado en el archivo', dataIndex: 'nombreArchivo', key: 'nombreArchivo', width: 240 },
      {
        title: 'Empleado en el sistema', key: 'empleado', width: 320,
        render: (_, r) => r.candidatos?.length > 1
          ? this.selectorCandidatos(r)
          : r.error
          ? <span style={{ color: '#cf1322' }}><WarningOutlined /> {r.error}</span>
          : (
            <span>
              {r.empleadoNombre}{' '}
              {r.criterio === 'Nombre' && <Tag color="orange" style={{ fontSize: 10 }}>por nombre</Tag>}
              {r.empleadoBaja && <Tag color="default" style={{ fontSize: 10 }}>baja</Tag>}
            </span>
          ),
      },
      { title: 'Departamento', dataIndex: 'departamento', key: 'departamento', width: 200 },
      {
        title: 'Percepciones', key: 'perc', width: 120, align: 'right',
        render: (_, r) => <span style={{ fontFamily: 'monospace' }}>{dinero(r.totales.percepciones)}</span>,
      },
      {
        title: 'Deducciones', key: 'ded', width: 120, align: 'right',
        render: (_, r) => <span style={{ fontFamily: 'monospace' }}>{dinero(r.totales.deducciones)}</span>,
      },
      {
        title: 'Neto', key: 'neto', width: 120, align: 'right',
        render: (_, r) => <span style={{ fontFamily: 'monospace' }}>{dinero(r.totales.neto)}</span>,
      },
      {
        title: 'Contra el archivo', key: 'cuadre', width: 220,
        render: (_, r) => r.totales.cuadra
          ? <span style={{ color: '#389e0d' }}><CheckCircleOutlined /> Cuadra</span>
          : (
            <span style={{ color: '#d46b08' }}>
              <WarningOutlined />{' '}
              {r.totales.difPercepciones ? `Percepciones ${dinero(r.totales.difPercepciones)} ` : ''}
              {r.totales.difDeducciones ? `Deducciones ${dinero(r.totales.difDeducciones)}` : ''}
            </span>
          ),
      },
    ]
  }

  render() {
    const {
      loading, importando, periodos, hojas, hojaSeleccionada,
      analisis, periodoSeleccionado, prenominasPorPeriodo, puedeGenerarPoliza, archivos,
    } = this.state

    const periodosOcupados = new Set(prenominasPorPeriodo.map((p) => String(p.periodoId)))
    const resumen = analisis?.resumen
    const faltantes = analisis && !analisis.error
      ? codigosFaltantes(analisis.preview, this.state.empleados)
      : []
    const puedeImportar = !!(analisis && !analisis.error && periodoSeleccionado
      && analisis.filas.length > 0 && analisis.conceptosIds.length > 0 && !importando)

    return (
      <Spin spinning={loading || importando} tip={importando ? 'Importando...' : 'Cargando...'}>
        <div style={{ padding: '0 8px 24px' }}>
          <Alert
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
            message="Carga de una nómina ya pagada"
            description={
              <span>
                Esta nómina <b>no se timbra</b> y <b>no mueve créditos de INFONAVIT, FONACOT ni
                movimientos permanentes</b> — su único efecto son los acumulados del ejercicio, y
                opcionalmente la póliza contable. Después de importar hay que presionar
                <b> Calcular</b> para que el motor derive el gravado y el exento de cada concepto,
                y luego <b>Aplicar histórico</b>.
                {!puedeGenerarPoliza && (
                  <div style={{ marginTop: 6 }}>
                    La póliza contable no va a estar disponible: falta el tipo de póliza o el
                    proyecto general en Configuración General de Nómina.
                  </div>
                )}
              </span>
            }
          />

          {/* ── Archivo y periodo ─────────────────────────────────────────── */}
          <Row gutter={16} style={{ marginBottom: 16 }}>
            <Col xs={24} sm={8}>
              <div style={{ fontSize: 12, color: '#888', marginBottom: 4 }}>1. Archivo de la lista de raya</div>
              <Upload
                showUploadList={{ showRemoveIcon: false, showPreviewIcon: false }}
                fileList={archivos}
                customRequest={this.onSubirArchivo.bind(this)}
                accept=".xlsx,.xls"
              >
                <ButtonItem icon={<UploadOutlined />}>Subir Excel</ButtonItem>
              </Upload>
            </Col>

            <Col xs={24} sm={8}>
              <div style={{ fontSize: 12, color: '#888', marginBottom: 4 }}>2. Hoja</div>
              <SelectItem
                style={{ width: '100%' }}
                placeholder="Elige la hoja"
                value={hojaSeleccionada || undefined}
                disabled={!hojas.length}
                onChange={(v) => this.onSeleccionarHoja(v)}
              >
                {hojas.map((h) => <Option value={h} key={h}>{h}</Option>)}
              </SelectItem>
            </Col>

            <Col xs={24} sm={8}>
              <div style={{ fontSize: 12, color: '#888', marginBottom: 4 }}>3. Periodo destino</div>
              <SelectItem
                style={{ width: '100%' }}
                placeholder="Buscar periodo..."
                showSearch
                filterOption={(input, opt) =>
                  String(opt?.children || '').toLowerCase().includes(input.toLowerCase())
                }
                onChange={(id) =>
                  this.setState({ periodoSeleccionado: periodos.find((p) => String(p._id) === String(id)) || null })
                }
              >
                {periodos.map((p) => (
                  <Option value={String(p._id)} key={String(p._id)} disabled={periodosOcupados.has(String(p._id))}>
                    {p.serie}-{p.numero} — {p.tipoPeriodoId?.nombre} ·{' '}
                    {moment(p.fechaInicio).format('DD/MM/YY')} al {moment(p.fechaFin).format('DD/MM/YY')}
                    {periodosOcupados.has(String(p._id)) ? ' (ya tiene prenómina)' : ''}
                  </Option>
                ))}
              </SelectItem>
            </Col>
          </Row>

          {/* ── Cabecera detectada del archivo ────────────────────────────── */}
          {analisis && !analisis.error && (
            <Alert
              type={this.cabeceraCoincide() ? 'success' : 'warning'}
              showIcon
              style={{ marginBottom: 16 }}
              message={
                <span>
                  Archivo: <b>{analisis.cabecera.empresa || 'sin nombre'}</b>
                  {analisis.cabecera.periodoTexto ? ` — ${analisis.cabecera.periodoTexto}` : ''}
                  {analisis.cabecera.rfc ? ` — RFC ${analisis.cabecera.rfc}` : ''}
                </span>
              }
              description={
                periodoSeleccionado
                  ? this.cabeceraCoincide()
                    ? 'Las fechas del archivo coinciden con el periodo seleccionado.'
                    : `Ojo: el periodo seleccionado va del ${moment(periodoSeleccionado.fechaInicio).format('DD/MM/YYYY')} al ${moment(periodoSeleccionado.fechaFin).format('DD/MM/YYYY')} y el archivo dice ${analisis.cabecera.fechaInicio} a ${analisis.cabecera.fechaFin}. Verifica que sea el periodo correcto.`
                  : 'Elige el periodo destino para comparar las fechas.'
              }
            />
          )}

          {/* ── Mapeo de columnas ─────────────────────────────────────────── */}
          {analisis && !analisis.error && (
            <>
              <h3 style={{ marginTop: 8 }}>Mapeo de columnas</h3>
              <p style={{ color: '#888', fontSize: 12, marginBottom: 8 }}>
                El tipo (percepción / deducción) se toma de si la columna está antes o después de
                <i> *TOTAL* *PERCEPCIONES*</i> en el archivo — por eso un mismo nombre puede aparecer
                dos veces con conceptos distintos. Una columna sin mapear no carga sus importes.
              </p>
              {/* Table de antd y no TableItem: TableItem fuerza pagination, scroll y
                  ellipsis en todas las columnas, y aquí hace falta ver los 22 renglones
                  de golpe con un Select completo en cada uno. */}
              <Table
                size="small"
                rowKey={(r) => r.indice}
                dataSource={analisis.mapeo}
                columns={this.columnasMapeo()}
                pagination={false}
                scroll={{ y: 340 }}
              />
            </>
          )}

          {/* ── Resumen + previsualización ────────────────────────────────── */}
          {analisis && !analisis.error && (
            <>
              <h3 style={{ marginTop: 24 }}>
                Empleados{' '}
                <Tag color="green">{resumen.emparejados} emparejados</Tag>
                {resumen.conError - resumen.porElegir > 0 && <Tag color="red">{resumen.conError - resumen.porElegir} sin empleado</Tag>}
                {resumen.porElegir > 0 && <Tag color="orange">{resumen.porElegir} por elegir</Tag>}
                {resumen.descuadrados > 0 && <Tag color="orange">{resumen.descuadrados} descuadrados</Tag>}
                {resumen.columnasSinMapear > 0 && <Tag color="red">{resumen.columnasSinMapear} columnas sin mapear</Tag>}
              </h3>
              {/* Ex-empleados que nunca se dieron de alta en Biwo: Importar Desde Software
                  Externo omite las bajas, así que sin esto no hay forma de cargarles la nómina. */}
              {faltantes.length > 0 && (
                <Alert
                  type="warning"
                  showIcon
                  style={{ marginBottom: 12 }}
                  message={`${faltantes.length} empleado(s) del archivo no existen en Biwo, ni activos ni deshabilitados`}
                  description={
                    this.props.permisoAgregarEmpleado
                      ? 'Si ya no trabajan en la empresa, créalos desde el Excel de empleados de tu software externo: quedan deshabilitados y listos para esta nómina.'
                      : 'Para crearlos deshabilitados desde el Excel de empleados del software externo hace falta el permiso de Agregar Empleado.'
                  }
                  action={this.props.permisoAgregarEmpleado && (
                    <ButtonItem size="small" onClick={() => this.setState({ modalFaltantesVisible: true })}>
                      Crear empleados faltantes
                    </ButtonItem>
                  )}
                />
              )}
              <TableItem
                size="small"
                rowKey={(r) => `${r.filaExcel}`}
                dataSource={analisis.preview}
                columns={this.columnasPreview()}
                defaultPageSize={500}
              />
            </>
          )}

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
            <ButtonItem onClick={() => this.props.onCancelar()}>Cancelar</ButtonItem>
            <ButtonItem type="primary" disabled={!puedeImportar} onClick={() => this.importar()}>
              Crear prenómina histórica
            </ButtonItem>
          </div>

          <ModalEmpleadosFaltantes
            visible={this.state.modalFaltantesVisible}
            faltantes={faltantes}
            empleados={this.state.empleados}
            onCerrar={() => this.setState({ modalFaltantesVisible: false })}
            onCreados={() => {
              this.setState({ modalFaltantesVisible: false })
              this.consultarCatalogos(this.analizar)
            }}
          />
        </div>
      </Spin>
    )
  }

  // El archivo trae las fechas como dd/mm/aaaa; se comparan contra el periodo elegido
  // para atajar el error más caro de todos: cargar la semana 31 sobre el periodo 30.
  cabeceraCoincide() {
    const { analisis, periodoSeleccionado } = this.state
    if (!analisis?.cabecera?.fechaInicio || !periodoSeleccionado) return false
    const ini = moment(analisis.cabecera.fechaInicio, 'DD/MM/YYYY', true)
    const fin = moment(analisis.cabecera.fechaFin, 'DD/MM/YYYY', true)
    if (!ini.isValid() || !fin.isValid()) return false
    return ini.isSame(moment(periodoSeleccionado.fechaInicio), 'day')
      && fin.isSame(moment(periodoSeleccionado.fechaFin), 'day')
  }
}

const mapStateToProps = (state) => ({
  headersToken: state.user.headersToken,
  empresaId: state.empresa.id,
  // El mismo de Agregar / Importar Empleados (Empleados.js): aquí también se dan altas.
  permisoAgregarEmpleado:
    state.user.tipo === 0 ||
    state.user.permisos.find(
      (valueTodos) => valueTodos.permisoId.toString() === '675096a4798d4e0043687255'
    )?.activado,
})

export default connect(mapStateToProps)(ImportarNominaHistorica)
